import { Validator, ValidationError } from './validate.js';
export const PLAN_STEP_TYPES = [
    'action',
    'assertion',
    'navigation',
    'extract',
    'wait',
    'setup',
    'teardown',
    'dependency',
];
export const PROJECT_TYPES = ['frontend', 'backend'];
export const PLAN_LIMITS = {
    name: 200,
    description: 4000,
    stepDescription: 2000,
    stepTarget: 2000,
    maxSteps: 200,
    minStepTimeoutMs: 1000,
    maxStepTimeoutMs: 600_000,
    maxTags: 20,
};
export const PLAN_KEYS = [
    '$schema',
    'version',
    'projectId',
    'environmentId',
    'type',
    'name',
    'description',
    'tags',
    'stepTimeoutMs',
    'planSteps',
];
export const STEP_KEYS = [
    'type',
    'description',
    'target',
    'optional',
    'value',
    'durationMs',
    'dependsOn',
    'locator',
    'assert',
    'action',
];
export const STRUCTURED_ACTIONS = ['click', 'fill', 'press', 'select', 'hover', 'clear'];
/** Validate a parsed plan file. Never throws; returns issues so `test lint` can print them all. */
export function validatePlan(input) {
    const v = new Validator();
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        v.error('$', `expected a plan object, received ${input === null ? 'null' : typeof input}`);
        return v.collected;
    }
    const plan = input;
    v.unknownKeys(plan, PLAN_KEYS, '$');
    if (plan['version'] !== undefined) {
        v.integer(plan['version'], '$.version', { min: 1, max: 2 });
    }
    const type = v.enum(plan['type'], PROJECT_TYPES, '$.type');
    v.string(plan['name'], '$.name', { max: PLAN_LIMITS.name });
    if (plan['description'] !== undefined) {
        v.string(plan['description'], '$.description', { max: PLAN_LIMITS.description });
    }
    if (plan['stepTimeoutMs'] !== undefined) {
        v.integer(plan['stepTimeoutMs'], '$.stepTimeoutMs', {
            min: PLAN_LIMITS.minStepTimeoutMs,
            max: PLAN_LIMITS.maxStepTimeoutMs,
        });
    }
    if (plan['tags'] !== undefined) {
        const tags = v.array(plan['tags'], '$.tags', { max: PLAN_LIMITS.maxTags }) ?? [];
        tags.forEach((tag, index) => v.string(tag, `$.tags[${index}]`, { max: 40 }));
    }
    for (const key of ['projectId', 'environmentId']) {
        if (plan[key] !== undefined)
            v.string(plan[key], `$.${key}`, { max: 80 });
    }
    const steps = v.array(plan['planSteps'], '$.planSteps', { min: 1, max: PLAN_LIMITS.maxSteps });
    if (steps) {
        steps.forEach((raw, index) => validateStep(v, raw, index, steps.length));
    }
    if (type === 'backend') {
        const nonDep = (steps ?? []).filter((step) => step?.type !== 'dependency');
        if (steps && steps.length > 0 && nonDep.length === 0) {
            v.warn('$.planSteps', 'a backend plan made only of dependency steps will always pass');
        }
    }
    return v.collected;
}
function validateStep(v, raw, index, total) {
    const path = `$.planSteps[${index}]`;
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        v.error(path, `expected a step object, received ${raw === null ? 'null' : typeof raw}`);
        return;
    }
    const step = raw;
    v.unknownKeys(step, STEP_KEYS, path);
    const type = v.enum(step['type'], PLAN_STEP_TYPES, `${path}.type`);
    v.string(step['description'], `${path}.description`, { max: PLAN_LIMITS.stepDescription });
    if (step['target'] !== undefined) {
        v.string(step['target'], `${path}.target`, { max: PLAN_LIMITS.stepTarget });
    }
    if (step['optional'] !== undefined && typeof step['optional'] !== 'boolean') {
        v.error(`${path}.optional`, 'expected a boolean');
    }
    if (step['action'] !== undefined) {
        v.enum(step['action'], STRUCTURED_ACTIONS, `${path}.action`);
    }
    if (step['locator'] !== undefined) {
        if (typeof step['locator'] !== 'object' || step['locator'] === null || Array.isArray(step['locator'])) {
            v.error(`${path}.locator`, 'expected a locator object');
        }
    }
    if (step['assert'] !== undefined) {
        if (typeof step['assert'] !== 'object' || step['assert'] === null || Array.isArray(step['assert'])) {
            v.error(`${path}.assert`, 'expected an assert object');
        }
    }
    const desc = typeof step['description'] === 'string' ? step['description'] : '';
    if (desc) {
        if (/\b(click|fill|submit|press|tap)\b/i.test(desc) && /\b(verify|assert|expect)\b/i.test(desc)) {
            v.error(path, 'step combines both action and assertion verbs; split into separate action and assertion steps');
        }
    }
    if (step['durationMs'] !== undefined) {
        const duration = v.integer(step['durationMs'], `${path}.durationMs`, { min: 0, max: 120_000 });
        if (duration !== undefined && type !== 'wait') {
            v.warn(`${path}.durationMs`, 'durationMs is only meaningful on a wait step');
        }
    }
    if (step['dependsOn'] !== undefined) {
        const deps = v.array(step['dependsOn'], `${path}.dependsOn`) ?? [];
        deps.forEach((dep, depIndex) => {
            const parsed = v.integer(dep, `${path}.dependsOn[${depIndex}]`, { min: 1, max: total });
            if (parsed !== undefined && parsed === index + 1) {
                v.error(`${path}.dependsOn[${depIndex}]`, 'a step cannot depend on itself');
            }
        });
    }
    if (type === 'navigation' && !step['target']) {
        v.warn(`${path}.target`, 'a navigation step without a target navigates relative to the current page');
    }
    if (type === 'assertion' && !step['target'] && !step['description']) {
        v.error(`${path}.description`, 'an assertion needs something to assert');
    }
}
/** Validate and narrow, throwing a ValidationError when the plan is unusable. */
export function parsePlan(input) {
    const issues = validatePlan(input);
    const fatal = issues.filter((issue) => issue.severity === 'error');
    if (fatal.length > 0) {
        throw new ValidationError(`invalid plan file (${fatal.length} error(s))`, issues);
    }
    return input;
}
/**
 * The exact skeleton `test create --plan-template` emits. Kept in core so the
 * CLI, the docs and the tests can never drift apart.
 */
export function planTemplate(projectId) {
    const plan = {
        $schema: 'https://verifyloop.dev/schemas/plan.schema.json',
        type: 'frontend',
        name: 'Login rejects an empty password',
        planSteps: [
            {
                type: 'action',
                description: 'Navigate to /login and submit the form with an empty password',
                target: '/login',
            },
            {
                type: 'assertion',
                description: 'Verify an inline error says the password is required',
                target: 'text=password is required',
            },
        ],
    };
    if (projectId)
        plan.projectId = projectId;
    return plan;
}
/**
 * Resolve `{{placeholders}}` against values captured by earlier extract steps.
 * Unresolved placeholders are returned untouched so a failure message can show
 * exactly what the agent wrote.
 */
export function resolvePlaceholders(input, values) {
    return input.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, path) => {
        const value = values.get(path);
        return value === undefined ? match : value;
    });
}
/** Every placeholder a plan refers to, so the runner can pre-check them. */
export function collectPlaceholders(plan) {
    const found = new Set();
    const pattern = /\{\{\s*([\w.]+)\s*\}\}/g;
    const scan = (text) => {
        if (!text)
            return;
        for (const match of text.matchAll(pattern))
            found.add(match[1]);
    };
    for (const step of plan.planSteps) {
        scan(step.description);
        scan(step.target);
    }
    return [...found];
}
//# sourceMappingURL=plan.js.map